import StatsPresenter from "./StatsPresenter";
import * as mediaQuery from "components/MediaQuery";
import { Layout } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import { useState, useMemo, useRef, useCallback, useEffect } from "react";
import {
  CardData,
  ChartLayoutResponse,
  StatsLayout,
} from "api/interfaces/dashboardInterface.interface";
import {
  applyChartLayoutFromGrid,
  applyChartLayoutFromSelection,
  CARD_INFOS,
  createCardData,
  createLayouts,
  INITIAL_CARD_LAYOUTS,
  removeChartFromLayout,
  replaceChartInLayout,
  sortCardData,
  cols,
  isSameCardDataItem,
  cardDataToLayout,
} from "utils/StatsUtil";
import { useNoticeHeight } from "components/NoticePovider";
import { useAppSelector } from "redux/hooks";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { getChartLayout, postChartLayout } from "api/dashboardAPI";
import { useQuery } from "react-query";
import useStatusMutation from "hook/useStatusMutation";
import Spinner from "components/atoms/loader/Spinner";
import { CustomError } from "api/interfaces/commonInterface.interface";
import useApiError from "hook/useApiError";
import { useProSidebar } from "react-pro-sidebar";

export default function StatsContainer(): JSX.Element {
  const { noticeHeight } = useNoticeHeight();
  const [isDraggable, setIsDraggable] = useState(false);
  const { handleErrorNotify, lang } = useApiError();
  const [showAddRemove, setShowAddRemove] = useState(false);
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const orginalCardLayouts = useRef<StatsLayout[]>(INITIAL_CARD_LAYOUTS);
  const [cardLayouts, setCardLayouts] =
    useState<StatsLayout[]>(INITIAL_CARD_LAYOUTS);
  const hydratedAccountRef = useRef<string | null>(null);
  const cardLayoutsRef = useRef<StatsLayout[]>(INITIAL_CARD_LAYOUTS);
  const isMobile = mediaQuery.useIsMobile();
  const { collapsed } = useProSidebar();
  const [includeSuspended, setIncludeSuspended] = useState<boolean>(false);

  // breakpoints와 cols를 메모이제이션하여 재생성 방지
  const breakpoints = useMemo(
    () => ({
      lg: mediaQuery.tabletMax,
      md: mediaQuery.mobileMin,
      sm: 0,
    }),
    []
  );

  const layoutCols = cols.lg;

  const { data, isFetched, isError, refetch } = useQuery(
    ["dashboardLayout", selectedAccount.accountId],
    () => getChartLayout(selectedAccount.accountId),
    {
      retry: 0,
      refetchOnWindowFocus: false,
    }
  );

  //기본레이아웃으로 정렬후 API 조회시 움직이는 현상 방지
  const layoutFromApi = useMemo((): StatsLayout[] | null => {
    if (!isFetched) return null;
    if (isError || !data?.result?.length) {
      return INITIAL_CARD_LAYOUTS.map((item) => ({ ...item })) as StatsLayout[];
    }
    return applyChartLayoutFromGrid(
      data.result.map((item: StatsLayout) => {
        const defaultGraphType = CARD_INFOS.find(
          (info) => info.i === item.i
        )?.graphType;
        return {
          ...item,
          graphType: item.graphType ?? defaultGraphType,
        };
      }) as StatsLayout[],
      [],
      { cols: layoutCols, replace: true }
    ) as StatsLayout[];
  }, [isFetched, isError, data, layoutCols]);

  const effectiveCardLayouts = useMemo(() => {
    if (!layoutFromApi) return INITIAL_CARD_LAYOUTS;
    if (hydratedAccountRef.current !== selectedAccount.accountId) {
      return layoutFromApi;
    }
    return cardLayouts;
  }, [layoutFromApi, cardLayouts, selectedAccount.accountId]);

  cardLayoutsRef.current = effectiveCardLayouts;

  const mutationUpdateLayout = useStatusMutation(postChartLayout, {
    onSuccess: (res: ChartLayoutResponse) => {
      refetch();
    },
    onError: (error: CustomError) => {
      handleErrorNotify(error, lang.label.dash.widget.update.fail);
    },
  });

  const [graphTypeOverrides, setGraphTypeOverrides] = useState<
    Record<string, string>
  >({});
  const cardDataRef = useRef<CardData[]>([]);
  const cardData: CardData[] = useMemo(() => {
    // isRole 필터링 적용
    const filteredCardInfos = CARD_INFOS.filter(
      (info) =>
        info.isRole.some((role) => selectedAccount.userPermission[role]) &&
        effectiveCardLayouts.some((layout) => layout.i === info.i)
    );
    const base = createCardData(
      "not-card",
      filteredCardInfos,
      effectiveCardLayouts
    );
    const prev = cardDataRef.current;

    const next = base.map((item) => {
      const graphType = item.graphType ?? graphTypeOverrides[item.key];
      const merged = { ...item, graphType };
      const old = prev.find((p) => p.key === item.key);
      if (old && isSameCardDataItem(old, merged)) return old;
      return merged;
    });

    cardDataRef.current = next;
    return next;
  }, [
    effectiveCardLayouts,
    selectedAccount.userPermission,
    graphTypeOverrides,
  ]);

  const onChangeLayout = useCallback(
    (layout: StatsLayout[]) => {
      setCardLayouts(layout);

      let payload = cardDataToLayout(layout);
      if (!mutationUpdateLayout.idle) return;
      mutationUpdateLayout.mutate({
        accountId: selectedAccount.accountId,
        payload: payload,
      });
    },
    [mutationUpdateLayout, selectedAccount.accountId]
  );
  const graphLayouts = useMemo(() => {
    // cardLayouts에서 "Count"가 아닌 항목만 필터링 (Summary 카드 제외)
    const filteredLayouts = effectiveCardLayouts.filter(
      (item) => !item.i.includes("Count")
    );
    return createLayouts(sortCardData(filteredLayouts), "index", 4, 2, 2);
  }, [effectiveCardLayouts]);

  const onAddLayout = (selectedChartList: Layout[]) => {
    const next = applyChartLayoutFromSelection(
      cardLayoutsRef.current,
      selectedChartList,
      layoutCols
    ) as StatsLayout[];
    onChangeLayout(next);
  };
  const onReplaceChart = useCallback(
    (currentChartId: string, newChartId: string) => {
      const next = replaceChartInLayout(
        cardLayoutsRef.current,
        currentChartId,
        newChartId
      ) as StatsLayout[];
      onChangeLayout(next);
    },
    [onChangeLayout]
  );

  const onRemoveChart = useCallback(
    (chartId: string) => {
      const next = removeChartFromLayout(
        cardLayoutsRef.current,
        chartId,
        layoutCols
      ) as StatsLayout[];
      onChangeLayout(next);
    },
    [onChangeLayout, layoutCols]
  );

  const onChangeDragAndResize = useCallback(
    (layout: StatsLayout[]) => {
      onChangeLayout(layout);
    },
    [onChangeLayout]
  );

  const onChangeGraphType = useCallback(
    (key: string, type: string) => {
      if (!mutationUpdateLayout.idle) return;
      setGraphTypeOverrides((prev) => ({
        ...prev,
        [key]: type,
      }));
      const next = cardLayouts.map((item) => {
        if (item.i === key) {
          return { ...item, graphType: type };
        }
        return item;
      });
      onChangeLayout(next);
    },
    [cardLayouts, mutationUpdateLayout.idle, onChangeLayout]
  );

  const hiddenCharts = useMemo(
    () =>
      CARD_INFOS.filter(
        (item) =>
          !cardData.some((info) => info.i === item.i) &&
          item.graphType !== "card" &&
          item.isRole.some((role) => selectedAccount.userPermission[role])
      ),
    [cardData, selectedAccount.userPermission]
  );

  const onToggleAddLayout = () => {
    setShowAddRemove(!showAddRemove);
    setIsDraggable(false);
  };

  useEffect(() => {
    if (!layoutFromApi) return;
    hydratedAccountRef.current = selectedAccount.accountId;
    setCardLayouts(cardDataToLayout(layoutFromApi));
    orginalCardLayouts.current = layoutFromApi.map((item) => ({ ...item }));
  }, [layoutFromApi, selectedAccount.accountId]);

  if (!layoutFromApi) return <Spinner />;

  return (
    <StatsPresenter
      cardData={cardData}
      graphLayouts={graphLayouts}
      breakpoints={breakpoints}
      cols={cols}
      noticeHeight={noticeHeight}
      isDraggable={isDraggable}
      cardLayouts={effectiveCardLayouts}
      onAddLayout={onAddLayout}
      isMobile={isMobile}
      hiddenCharts={hiddenCharts}
      onReplaceChart={onReplaceChart}
      onRemoveChart={onRemoveChart}
      onChangeGraphType={onChangeGraphType}
      onToggleAddLayout={onToggleAddLayout}
      showAddRemove={showAddRemove}
      collapsed={collapsed}
      includeSuspended={includeSuspended}
      setIncludeSuspended={setIncludeSuspended}
      onChangeDragAndResize={onChangeDragAndResize}
    />
  );
}
